import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord"
import StopOutlinedIcon from "@mui/icons-material/StopOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Autocomplete from "@mui/material/Autocomplete"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import CardHeader from "@mui/material/CardHeader"
import Collapse from "@mui/material/Collapse"
import Divider from "@mui/material/Divider"
import FormControl from "@mui/material/FormControl"
import FormControlLabel from "@mui/material/FormControlLabel"
import FormLabel from "@mui/material/FormLabel"
import IconButton from "@mui/material/IconButton"
import MenuItem from "@mui/material/MenuItem"
import Radio from "@mui/material/Radio"
import RadioGroup from "@mui/material/RadioGroup"
import Select from "@mui/material/Select"
import Switch from "@mui/material/Switch"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { forwardRef, useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { parseApiErrorMessage } from "../../../common/apiErrors"
import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import {
  TemplateGroupTabs,
  useTemplatePickerTab,
} from "../components/templateGroups"
import { selectCRMSettings, updateCRMSettings } from "../reducer"
import {
  ADVANCED_FIELDS,
  AMD_ACTION_OPTIONS,
  AMD_MODE_OPTIONS,
  DEFAULT_AMD_CONFIG,
  HOUR_OPTIONS,
  TUNING_FIELDS,
  TWILIO_DEFAULT_AMD_CONFIG,
  amdFormFromSettings,
  amdSettingsBlocker,
  buildAmdSettingsPatch,
  isAmdFormDirty,
} from "./amdSettings"
import {
  GREETING_MAX_RECORD_SECONDS,
  convertBlobToWavFile,
  pickMediaRecorderMimeType,
  validateGreetingFile,
} from "./greetingAudio"

const VOICEMAIL_DROPS_URL = "/crm/api/voicemail-drops/"

function normalizeTemplateList(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.results)) return data.results
  return []
}

function mergeTemplates(inboxTemplates, campaigns) {
  const byId = new Map()
  ;[...inboxTemplates, ...campaigns].forEach((item) => {
    if (item?.id != null) {
      byId.set(item.id, { ...item, label: item.name || `Template ${item.id}` })
    }
  })
  return [...byId.values()]
}

/**
 * Account defaults for Power Dialer answering-machine detection: what happens
 * when voicemail picks up, plus the detection tuning and the voicemail
 * recordings a session can drop.
 */
const AmdSettingsCard = forwardRef(function AmdSettingsCard(
  { onStatusMessage },
  ref
) {
  const dispatch = useDispatch()
  const axios = useAuthorizedAxiosClient()
  const crmSettings = useSelector(selectCRMSettings)
  const settings = crmSettings.settings

  const [form, setForm] = useState(() => amdFormFromSettings(settings))
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState("")
  const [tuningOpen, setTuningOpen] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [templates, setTemplates] = useState([])
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [recordings, setRecordings] = useState([])

  const dirty = isAmdFormDirty(settings, form)
  const dirtyRef = useRef(dirty)
  dirtyRef.current = dirty

  // Re-seed from the server only while the agent has no unsaved edits.
  useEffect(() => {
    if (!dirtyRef.current) {
      setForm(amdFormFromSettings(settings))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    settings?.amd_enabled,
    settings?.amd_machine_action,
    settings?.amd_mode,
    settings?.amd_sms_campaign,
    settings?.amd_voicemail_recording,
    settings?.amd_config,
  ])

  const notify = useCallback(
    (message, severity) => onStatusMessage?.(message, severity),
    [onStatusMessage]
  )

  const loadRecordings = useCallback(() => {
    return axios
      .get(VOICEMAIL_DROPS_URL)
      .then(({ data }) => {
        setRecordings(Array.isArray(data) ? data : [])
      })
      .catch(() => setRecordings([]))
  }, [axios])

  useEffect(() => {
    loadRecordings()
  }, [loadRecordings])

  useEffect(() => {
    let cancelled = false
    setTemplatesLoading(true)
    Promise.all([
      axios
        .get("/crm/api/concierge-sms/inbox/templates/")
        .then(({ data }) => normalizeTemplateList(data))
        .catch(() => []),
      axios
        .get("/workflows/api/sms/select-campaign/")
        .then(({ data }) => normalizeTemplateList(data))
        .catch(() => []),
    ])
      .then(([inboxTemplates, campaigns]) => {
        if (!cancelled) setTemplates(mergeTemplates(inboxTemplates, campaigns))
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [axios])

  function patch(changes) {
    setSaveError("")
    setForm((current) => ({ ...current, ...changes }))
  }

  function patchConfig(changes) {
    setSaveError("")
    setForm((current) => ({
      ...current,
      amd_config: { ...current.amd_config, ...changes },
    }))
  }

  function applyProfile(profile) {
    patch({
      amd_config: {
        ...(profile === "twilio_default"
          ? TWILIO_DEFAULT_AMD_CONFIG
          : DEFAULT_AMD_CONFIG),
        text_quiet_hours: { ...form.amd_config.text_quiet_hours },
      },
    })
  }

  const smsReady = settings?.amd_sms_ready
  const blocker = amdSettingsBlocker(form, {
    smsReady,
    recordingCount: recordings.length,
  })

  function handleSave() {
    const body = buildAmdSettingsPatch(settings, form)
    if (!Object.keys(body).length) return
    setIsSaving(true)
    setSaveError("")
    axios
      .patch("/crm/api/offer-settings/", body)
      .then(({ data }) => {
        dispatch(updateCRMSettings(data))
        notify("Answering machine detection saved.", "success")
      })
      .catch((error) => {
        setSaveError(
          parseApiErrorMessage(
            error?.response?.data,
            "Could not save these settings. Please try again."
          )
        )
      })
      .finally(() => setIsSaving(false))
  }

  const selectedTemplate =
    templates.find((item) => item.id === form.sms_campaign_id) || null
  const {
    tab: templateTab,
    setTab: setTemplateTab,
    options: templateOptions,
  } = useTemplatePickerTab(templates)
  const quietHours = form.amd_config.text_quiet_hours

  return (
    <Card ref={ref} variant="outlined" sx={{ width: "100%" }}>
      <CardHeader
        title="Answering machine detection"
        titleTypographyProps={{ variant: "h6", fontWeight: 700 }}
        subheader="Power Dialer detects voicemail and acts for you, so agents only talk to people."
        sx={{
          textAlign: "left",
          alignItems: "flex-start",
          pb: 1,
          "& .MuiCardHeader-content": { overflow: "visible" },
        }}
        subheaderTypographyProps={{
          variant: "body2",
          sx: { textAlign: "left", mt: 0.5 },
        }}
      />
      <CardContent sx={{ pt: 0, px: 2, pb: 2, "&:last-child": { pb: 2 } }}>
        <Column sx={{ gap: 2, alignItems: "stretch" }}>
          <FormControlLabel
            control={
              <Switch
                checked={form.amd_enabled}
                onChange={(e) => patch({ amd_enabled: e.target.checked })}
              />
            }
            label={
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Detect answering machines by default
              </Typography>
            }
          />

          <Collapse in={form.amd_enabled} unmountOnExit>
            <Column sx={{ gap: 2 }}>
              <FormControl>
                <FormLabel sx={{ fontWeight: 700, fontSize: 14 }}>
                  When a voicemail is detected
                </FormLabel>
                <RadioGroup
                  value={form.amd_machine_action}
                  onChange={(e) =>
                    patch({ amd_machine_action: e.target.value })
                  }
                >
                  {AMD_ACTION_OPTIONS.map((action) => (
                    <FormControlLabel
                      key={action.value}
                      value={action.value}
                      control={<Radio size="small" />}
                      label={
                        <Column>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {action.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {action.help}
                          </Typography>
                        </Column>
                      }
                    />
                  ))}
                </RadioGroup>
              </FormControl>

              {form.amd_machine_action === "text" ? (
                <Column sx={{ gap: 0.75 }}>
                  <TemplateGroupTabs
                    tab={templateTab}
                    onChange={setTemplateTab}
                  />
                  <Autocomplete
                    value={selectedTemplate}
                    onChange={(_, option) =>
                      patch({ sms_campaign_id: option?.id ?? null })
                    }
                    options={templateOptions}
                    loading={templatesLoading}
                    getOptionLabel={(option) =>
                      option.label || option.name || ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Text to send"
                        placeholder="Select an SMS template"
                      />
                    )}
                    noOptionsText={
                      templatesLoading
                        ? "Loading templates…"
                        : "No SMS templates found"
                    }
                  />
                  {smsReady === false ? (
                    <Typography variant="body2" color="warning.main">
                      Finish SMS setup above before the dialer can text leads.
                    </Typography>
                  ) : null}
                </Column>
              ) : null}

              {form.amd_machine_action === "voicemail" ? (
                <FormControl size="small" fullWidth>
                  <FormLabel sx={{ fontWeight: 700, fontSize: 14, mb: 0.5 }}>
                    Voicemail to leave
                  </FormLabel>
                  <Select
                    displayEmpty
                    value={form.voicemail_recording_id ?? ""}
                    onChange={(e) =>
                      patch({ voicemail_recording_id: e.target.value || null })
                    }
                  >
                    <MenuItem value="">
                      <em>Choose a recording</em>
                    </MenuItem>
                    {recordings.map((recording) => (
                      <MenuItem key={recording.id} value={recording.id}>
                        {recording.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}

              <FormControl>
                <FormLabel sx={{ fontWeight: 700, fontSize: 14 }}>
                  Detection style
                </FormLabel>
                <RadioGroup
                  value={form.amd_mode}
                  onChange={(e) => patch({ amd_mode: e.target.value })}
                >
                  {AMD_MODE_OPTIONS.map((mode) => (
                    <FormControlLabel
                      key={mode.value}
                      value={mode.value}
                      control={<Radio size="small" />}
                      label={
                        <Column>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {mode.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {mode.help}
                          </Typography>
                        </Column>
                      }
                    />
                  ))}
                </RadioGroup>
              </FormControl>

              <Column sx={{ gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(quietHours.enabled)}
                      onChange={(e) =>
                        patchConfig({
                          text_quiet_hours: {
                            ...quietHours,
                            enabled: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Do not auto-text overnight
                    </Typography>
                  }
                />
                <Collapse in={Boolean(quietHours.enabled)} unmountOnExit>
                  <Row
                    sx={{ gap: 1.5, alignItems: "center", flexWrap: "wrap" }}
                  >
                    <HourSelect
                      label="Quiet from"
                      value={quietHours.start_hour}
                      onChange={(hour) =>
                        patchConfig({
                          text_quiet_hours: { ...quietHours, start_hour: hour },
                        })
                      }
                    />
                    <HourSelect
                      label="Quiet until"
                      value={quietHours.end_hour}
                      onChange={(hour) =>
                        patchConfig({
                          text_quiet_hours: { ...quietHours, end_hour: hour },
                        })
                      }
                    />
                  </Row>
                </Collapse>
              </Column>

              <Divider />

              <Button
                size="small"
                onClick={() => setTuningOpen((open) => !open)}
                endIcon={
                  <ExpandMoreIcon
                    sx={{
                      transform: tuningOpen ? "rotate(180deg)" : "none",
                      transition: "transform .15s",
                    }}
                  />
                }
                sx={{
                  alignSelf: "flex-start",
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                Detection tuning
              </Button>
              <Collapse in={tuningOpen} unmountOnExit>
                <Column sx={{ gap: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Defaults come from the Asterisk profile our ViciDial build
                    runs. Change these once you have real numbers from the call
                    logs.
                  </Typography>
                  <Row sx={{ gap: 1.5, flexWrap: "wrap" }}>
                    {TUNING_FIELDS.map((field) => (
                      <NumberField
                        key={field.key}
                        field={field}
                        value={form.amd_config[field.key]}
                        onChange={(value) =>
                          patchConfig({ [field.key]: value })
                        }
                      />
                    ))}
                  </Row>
                  <Row sx={{ gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => applyProfile("asterisk_reference")}
                      sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                      Reset to reference profile
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => applyProfile("twilio_default")}
                      sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                      Use Twilio defaults
                    </Button>
                  </Row>
                  <Button
                    size="small"
                    onClick={() => setAdvancedOpen((open) => !open)}
                    endIcon={
                      <ExpandMoreIcon
                        sx={{
                          transform: advancedOpen ? "rotate(180deg)" : "none",
                          transition: "transform .15s",
                        }}
                      />
                    }
                    sx={{
                      alignSelf: "flex-start",
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    Advanced (reference values)
                  </Button>
                  <Collapse in={advancedOpen} unmountOnExit>
                    <Column sx={{ gap: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Twilio&apos;s detector has no equivalent for these. They
                        are kept for the detector we can run ourselves later,
                        and changing them has no effect today.
                      </Typography>
                      <Row sx={{ gap: 1.5, flexWrap: "wrap" }}>
                        {ADVANCED_FIELDS.map((field) => (
                          <NumberField
                            key={field.key}
                            field={field}
                            value={form.amd_config[field.key]}
                            onChange={(value) =>
                              patchConfig({ [field.key]: value })
                            }
                          />
                        ))}
                      </Row>
                    </Column>
                  </Collapse>
                </Column>
              </Collapse>
            </Column>
          </Collapse>

          {blocker ? <Alert severity="warning">{blocker}</Alert> : null}
          {saveError ? (
            <Typography variant="body2" color="error">
              {saveError}
            </Typography>
          ) : null}

          <Row sx={{ justifyContent: "flex-end" }}>
            <LoadingButton
              variant="contained"
              color="primary"
              loading={isSaving}
              disabled={!dirty || Boolean(blocker)}
              onClick={handleSave}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Save
            </LoadingButton>
          </Row>

          <Divider />

          <VoicemailDropManager
            recordings={recordings}
            onChanged={loadRecordings}
            onStatusMessage={notify}
            onDeleted={(id) => {
              if (form.voicemail_recording_id === id) {
                patch({ voicemail_recording_id: null })
              }
            }}
          />
        </Column>
      </CardContent>
    </Card>
  )
})

function HourSelect({ label, value, onChange }) {
  return (
    <FormControl size="small" sx={{ minWidth: 140 }}>
      <FormLabel sx={{ fontSize: 12, mb: 0.5 }}>{label}</FormLabel>
      <Select value={value} onChange={(e) => onChange(Number(e.target.value))}>
        {HOUR_OPTIONS.map((hour) => (
          <MenuItem key={hour.value} value={hour.value}>
            {hour.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

function NumberField({ field, value, onChange }) {
  return (
    <TextField
      size="small"
      type="number"
      label={field.label}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ min: field.min, max: field.max }}
      sx={{ minWidth: 260, flex: "1 1 260px" }}
    />
  )
}

/** Record or upload the messages a session can drop into a voicemail. */
function VoicemailDropManager({
  recordings,
  onChanged,
  onStatusMessage,
  onDeleted,
}) {
  const axios = useAuthorizedAxiosClient()
  const [name, setName] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [recordingSeconds, setRecordingSeconds] = useState(0)
  const [pendingFile, setPendingFile] = useState(null)
  const [deletingId, setDeletingId] = useState(null)
  const fileInputRef = useRef(null)
  const mediaRecorderRef = useRef(null)
  const mediaStreamRef = useRef(null)
  const chunksRef = useRef([])
  const timerRef = useRef(null)

  const stopMediaStream = useCallback(() => {
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop())
    mediaStreamRef.current = null
  }, [])

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const stopRecording = useCallback(() => {
    clearTimer()
    setIsRecording(false)
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop()
    }
  }, [clearTimer])

  useEffect(
    () => () => {
      clearTimer()
      stopMediaStream()
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop()
      }
    },
    [clearTimer, stopMediaStream]
  )

  async function finalizeRecording() {
    const blob = new Blob(chunksRef.current, {
      type: chunksRef.current[0]?.type || "audio/webm",
    })
    chunksRef.current = []
    try {
      const wavFile = await convertBlobToWavFile(blob, "voicemail-drop.wav")
      setPendingFile(wavFile)
    } catch (error) {
      onStatusMessage(
        error?.message || "Could not process that recording. Try again.",
        "error"
      )
    }
  }

  async function handleStartRecording() {
    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === "undefined"
    ) {
      onStatusMessage(
        "Recording is not supported in this browser. Upload a file instead.",
        "error"
      )
      return
    }
    const mimeType = pickMediaRecorderMimeType()
    if (!mimeType) {
      onStatusMessage(
        "Recording is not supported in this browser. Upload a file instead.",
        "error"
      )
      return
    }
    chunksRef.current = []
    setPendingFile(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      mediaStreamRef.current = stream
      const recorder = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = recorder
      recorder.ondataavailable = (event) => {
        if (event.data?.size) chunksRef.current.push(event.data)
      }
      recorder.onstop = () => {
        stopMediaStream()
        finalizeRecording()
      }
      recorder.onerror = () => {
        stopRecording()
        onStatusMessage("Recording failed. Please try again.", "error")
      }
      recorder.start()
      setIsRecording(true)
      setRecordingSeconds(0)
      timerRef.current = window.setInterval(() => {
        setRecordingSeconds((prev) => {
          const next = prev + 1
          if (next >= GREETING_MAX_RECORD_SECONDS) stopRecording()
          return next
        })
      }, 1000)
    } catch (_) {
      stopMediaStream()
      onStatusMessage(
        "Microphone access is required to record. Check browser permissions or upload a file.",
        "error"
      )
    }
  }

  function handleFileChosen(event) {
    const file = event.target.files?.[0]
    event.target.value = ""
    if (!file) return
    const validationError = validateGreetingFile(file)
    if (validationError) {
      onStatusMessage(validationError, "error")
      return
    }
    setPendingFile(file)
  }

  function handleSave() {
    if (!pendingFile) return
    const trimmed = name.trim()
    if (!trimmed) {
      onStatusMessage("Give this voicemail a name.", "error")
      return
    }
    const formData = new FormData()
    formData.append("name", trimmed)
    formData.append("audio", pendingFile)
    setIsSaving(true)
    axios
      .post(VOICEMAIL_DROPS_URL, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(() => {
        setName("")
        setPendingFile(null)
        onStatusMessage("Voicemail recording saved.", "success")
        return onChanged()
      })
      .catch((error) => {
        onStatusMessage(
          parseApiErrorMessage(
            error?.response?.data,
            "Could not save this recording."
          ),
          "error"
        )
      })
      .finally(() => setIsSaving(false))
  }

  function handleDelete(recording) {
    setDeletingId(recording.id)
    axios
      .delete(`${VOICEMAIL_DROPS_URL}${recording.id}/`)
      .then(() => {
        onDeleted?.(recording.id)
        onStatusMessage("Voicemail recording deleted.", "success")
        return onChanged()
      })
      .catch((error) => {
        onStatusMessage(
          parseApiErrorMessage(
            error?.response?.data,
            "Could not delete this recording."
          ),
          "error"
        )
      })
      .finally(() => setDeletingId(null))
  }

  return (
    <Column sx={{ gap: 1.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        Voicemail recordings
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Messages the dialer can leave when a voicemail picks up. A session picks
        one when it starts.
      </Typography>

      {recordings.length ? (
        <Column sx={{ gap: 0.75 }}>
          {recordings.map((recording) => (
            <Row
              key={recording.id}
              sx={{
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 0.85,
                borderRadius: 1.5,
                bgcolor: "colors.gray50",
                border: "1px solid",
                borderColor: "colors.gray200",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}
                noWrap
                title={recording.name}
              >
                {recording.name}
              </Typography>
              {recording.duration_seconds ? (
                <Typography variant="caption" color="text.secondary">
                  {recording.duration_seconds}s
                </Typography>
              ) : null}
              <IconButton
                size="small"
                aria-label={`Delete ${recording.name}`}
                disabled={deletingId === recording.id}
                onClick={() => handleDelete(recording)}
              >
                <DeleteOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            </Row>
          ))}
        </Column>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No recordings yet.
        </Typography>
      )}

      <Row sx={{ gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          size="small"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Standard drop"
          inputProps={{ maxLength: 128 }}
          sx={{ minWidth: 220, flex: "1 1 220px" }}
        />
        {isRecording ? (
          <Button
            size="small"
            variant="contained"
            color="error"
            startIcon={<StopOutlinedIcon />}
            onClick={stopRecording}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Stop ({recordingSeconds}s)
          </Button>
        ) : (
          <Button
            size="small"
            variant="outlined"
            startIcon={<FiberManualRecordIcon color="error" />}
            onClick={handleStartRecording}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Record
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          onClick={() => fileInputRef.current?.click()}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Upload MP3 / WAV
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".mp3,.wav,audio/mpeg,audio/wav"
          onChange={handleFileChosen}
          style={{ display: "none" }}
        />
        <LoadingButton
          size="small"
          variant="contained"
          loading={isSaving}
          disabled={!pendingFile || !name.trim()}
          onClick={handleSave}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Save recording
        </LoadingButton>
      </Row>
      {pendingFile ? (
        <Typography variant="body2" color="success.main">
          Ready to save: {pendingFile.name}
        </Typography>
      ) : null}
    </Column>
  )
}

export default AmdSettingsCard
